import React, { useState } from "react";
import PhotographerFilters from "./PhotographerFilters";
import PhotographerTable   from "./PhotographerTable";
import useRoleAccordion from "./Hooks/useRoleAccordion";
// import useRoleAccordion    from "../hooks/useRoleAccordion";

/**
 * RoleAccordion — generic accordion for any assignable role.
 *
 * Props:
 *  title        string   — accordion heading e.g. "Videographers"
 *  bookingId    string
 *  isOpen       boolean  — modal open state (passed down for effects)
 *  services     array    — for Category filter dropdown
 *  fetchListAPI fn       — paginated list API
 *  fetchAllAPI  fn       — fetch-all for select-all
 *  fetchSortAPI fn|null  — sort/filter API (pass null to hide filter bar)
 *  onSelectionChange fn  — (roleKey, ids) => void  — notifies parent of selections
 *  roleKey      string   — unique key e.g. "videographer"
 */
const RoleAccordion = ({
    title,
    bookingId,
    isOpen,
    services = [],
    fetchListAPI,
    fetchAllAPI,
    fetchSortAPI = null,
    onSelectionChange,
    roleKey,
    hourlyPackages = [],
    selectedPackageIndex = 0,
    onPackageSelect,
    onAction,
    roleSelections = {},
    allPackagesSelections = {},
    refreshVersion = 0,
    showFilters = false,
    customizeMode = false,
    baseAmountOverride = 0,
}) => {
    const isFlatLayout = customizeMode;
    const [accordionOpen, setAccordionOpen] = useState(false);
    const isExpanded = isFlatLayout || accordionOpen;
    const [photographerOverrides, setPhotographerOverrides] = useState({});

    const {
        filteredItems,
        search,
        setSearch,
        selectAllChecked,
        loading,
        isLock,
        page,
        setPage,
        totalPages,
        filters,
        selectedIds,
        LIMIT,
        handleSelectAll,
        handleToggle,
        handleFilterChange,
        resetFilters,
    } = useRoleAccordion({ 
        isOpen, 
        bookingId, 
        fetchListAPI, 
        fetchAllAPI, 
        fetchSortAPI, 
        selectedPackageIndex, 
        refreshVersion,
        isExpanded,
    });

    // 1. Identify which IDs are already assigned specifically to THIS package and THIS role (DB Saved)
    const currentPkg = hourlyPackages[selectedPackageIndex];
    const assignedIdsForRole = (() => {
        if (!currentPkg) return [];
        if (roleKey === 'photographer') return currentPkg.assignedPhotographers || [];
        if (roleKey === 'videographer') return currentPkg.assignedVideographers || [];
        if (roleKey === 'editor')       return currentPkg.assignedEditors       || [];
        if (roleKey === 'lightingSetup') return currentPkg.assignedLighting      || [];
        return [];
    })();

    // Calculate baseAmount for commission calculation
    // Since we assign to a specific role, we use that role's price divided by its quantity.
    const getBaseAmount = () => {
        if (customizeMode && baseAmountOverride > 0) {
            return baseAmountOverride;
        }

        const matchingServices = (currentPkg?.services || []).filter(s => {
            const name = (s.name || "").toLowerCase();
            const key = roleKey.toLowerCase();
            if (key === 'lightingsetup') return name.includes('lighting');
            if (key === 'editor') return name.includes('edit');
            return name.includes(key);
        });

        const roleTotalPrice = matchingServices.reduce((sum, s) => sum + (s.price || 0), 0);
        const roleTotalQty = matchingServices.reduce((sum, s) => sum + (s.qty || 1), 0);

        if (roleTotalQty > 0 && roleTotalPrice > 0) {
            return Math.round(roleTotalPrice / roleTotalQty);
        }

        // Fallback to total package divided by total qty if not matching
        const packageTotal = (currentPkg?.services || []).reduce((sum, s) => sum + (s.price || 0), 0);
        const packageQty = (currentPkg?.services || []).reduce((sum, s) => sum + (s.qty || 1), 0);
        if (packageQty > 0 && packageTotal > 0) {
            return Math.round(packageTotal / packageQty);
        }
        if (currentPkg?.subTotal > 0) {
            const qty = packageQty > 0 ? packageQty : 1;
            return Math.round(currentPkg.subTotal / qty);
        }
        return Math.round(packageTotal || currentPkg?.subTotal || 0);
    };
    const baseAmount = getBaseAmount();

    // 2. Identify IDs selected in OTHER ROLES within the SAME package (Uncommitted session state)
    // We only care about other roles in the CURRENTLY SELECTED package
    const currentPkgRoles = allPackagesSelections[selectedPackageIndex] || {};
    const otherSelectedIds = Object.entries(currentPkgRoles).flatMap(([rKey, ids]) => {
        if (rKey === roleKey) return []; // skip current role
        return ids;
    });

    // 3. Identify IDs assigned in OTHER packages (request accepted)
    const cleanId = (id) => (id?._id || id || "").toString();
    const otherPackagesAssignedIds = (hourlyPackages || []).flatMap((pkg, idx) => {
        if (idx === selectedPackageIndex) return [];
        return [
            ...(pkg.assignedEditors || []),
            ...(pkg.assignedPhotographers || []),
            ...(pkg.assignedVideographers || []),
            ...(pkg.assignedLighting || [])
        ].map(cleanId).filter(Boolean);
    });

    // 4. Enrich items with explicit status flags (overriding backend's generic isAssigned)
    const enrichedItems = filteredItems.map(p => {
        const pId = cleanId(p._id);
        const assignedIds = assignedIdsForRole.map(cleanId);
        
        let invitedIds = [];
        if (currentPkg) {
            if (roleKey === 'photographer') invitedIds = currentPkg.invitedPhotographers || [];
            else if (roleKey === 'videographer') invitedIds = currentPkg.invitedVideographers || [];
            else if (roleKey === 'editor')       invitedIds = currentPkg.invitedEditors       || [];
            else if (roleKey === 'lightingSetup') invitedIds = currentPkg.invitedLighting      || [];
        }
        invitedIds = invitedIds.map(cleanId);

        return {
            ...p,
            isAssigned: customizeMode ? p.isAssigned : assignedIds.includes(pId),
            isInvited: customizeMode ? p.isInvited : invitedIds.includes(pId),
            isAssignedToOtherPkg: customizeMode ? false : otherPackagesAssignedIds.includes(pId)
        };
    });

    // 5. Check if ANY assigned person in this role+package is a studio → plan is fully fulfilled
    const isStudioFulfilled = enrichedItems.some(p => p.isAssigned && p.hasStudio === true);
    const effectiveIsLock = isLock || isStudioFulfilled;

    const visibleSelectedIds = selectedIds.filter(id => enrichedItems.some(item => cleanId(item._id) === id));

    // Notify parent whenever selection changes
    React.useEffect(() => {
        // Compute details dynamically to keep them in sync with selectedIds
        const details = visibleSelectedIds.map(id => {
            const p = enrichedItems.find(item => cleanId(item._id) === id);
            const commission = photographerOverrides[id]?.commission !== undefined ? photographerOverrides[id].commission : (p?.commissionPercentage ?? 0);
            
            let autoAmount = "";
            const pct = parseFloat(commission);
            const veroCommission = baseAmount * (pct / 100);
            if (p?.gstRegistered) {
                autoAmount = Math.round(baseAmount - veroCommission);
            } else {
                autoAmount = Math.round((baseAmount / 1.18) - veroCommission);
            }

            const amount = photographerOverrides[id]?.amount !== undefined ? photographerOverrides[id].amount : autoAmount;
            const currentPkg = hourlyPackages[selectedPackageIndex];
            return {
                photographerId: id,
                amount: String(amount),
                commission: String(commission),
                packageIndex: selectedPackageIndex,
                planHours: currentPkg?.hours || "",
                planCategory: currentPkg?.category || "",
                roleKey,
            };
        });

        onSelectionChange?.(roleKey, visibleSelectedIds, details);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visibleSelectedIds.length, photographerOverrides, baseAmount]);

    const handleAction = (actionType) => {
        const photographerDetails = visibleSelectedIds.map(id => {
            const p = enrichedItems.find(item => cleanId(item._id) === id);
            const commission = photographerOverrides[id]?.commission !== undefined ? photographerOverrides[id].commission : (p?.commissionPercentage ?? 0);
            
            let autoAmount = "";
            const pct = parseFloat(commission);
            const veroCommission = baseAmount * (pct / 100);
            if (p?.gstRegistered) {
                autoAmount = Math.round(baseAmount - veroCommission);
            } else {
                autoAmount = Math.round((baseAmount / 1.18) - veroCommission);
            }

            const amount = photographerOverrides[id]?.amount !== undefined ? photographerOverrides[id].amount : autoAmount;
            const currentPkg = hourlyPackages[selectedPackageIndex];
            return {
                photographerId: id,
                amount: String(amount),
                commission: String(commission),
                packageIndex: selectedPackageIndex,
                planHours: currentPkg?.hours || "",
                planCategory: currentPkg?.category || "",
                roleKey,
            };
        });
        onAction?.(roleKey, actionType, photographerDetails);
    };

    const selectedBadge =
        visibleSelectedIds.length > 0 ? (
            <span className="role-section-selected-badge">
                {visibleSelectedIds.length} selected
            </span>
        ) : null;

    return (
        <div
            className={`form-group hourly-role-accordion-item${
                customizeMode ? " customize-hourly-role-section" : ""
            }`}
            style={customizeMode ? undefined : { gridColumn: "span 2" }}
        >
            {isFlatLayout ? (
                <div className="role-section-flat-header">
                    <h4 className="role-section-flat-title">{title}</h4>
                    {selectedBadge}
                </div>
            ) : (
                <div
                    className="accordion-header hourly-role-accordion-header"
                    onClick={() => setAccordionOpen((v) => !v)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            setAccordionOpen((v) => !v);
                        }
                    }}
                    aria-expanded={accordionOpen}
                >
                    <label>
                        {title}
                        {selectedBadge}
                    </label>
                    <span className="accordion-chevron" aria-hidden="true">
                        {accordionOpen ? "▲" : "▼"}
                    </span>
                </div>
            )}

            {isExpanded && (
                <div
                    className={`photographer-accordion-content${
                        customizeMode ? " photographer-accordion-content--full" : ""
                    }${isFlatLayout ? " photographer-accordion-content--flat float-booking-staff-panel" : ""}`}
                >
                    {!isFlatLayout && (
                        <label
                            style={{
                                fontWeight: "600",
                                marginBottom: "8px",
                                display: "block",
                            }}
                        >
                            Select {title} *
                        </label>
                    )}

                    <div
                        className={
                            isFlatLayout ? "staff-select-toolbar" : undefined
                        }
                    >
                    {/* Search */}
                    <div className="search-bar-container">
                        <input
                            type="text"
                            className="photographer-search-input"
                            placeholder={`Search ${title} by name or phone`}
                            value={search}
                            onChange={(e) => {
                                setSearch(e.target.value);
                                setPage(1);
                            }}
                            disabled={effectiveIsLock}
                        />
                    </div>

                    {/* Filters — shown when fetchSortAPI is provided OR showFilters is true */}
                    {(fetchSortAPI || showFilters) && (
                        <PhotographerFilters
                            filters={filters}
                            services={services}
                            isLock={effectiveIsLock}
                            onFilterChange={handleFilterChange}
                            onReset={resetFilters}
                            roleKey={roleKey}
                        />
                    )}

                    {/* Max Selection Info */}
                    {!customizeMode && (() => {
                        const currentPkg = hourlyPackages[selectedPackageIndex];
                        const matchingServices = (currentPkg?.services || []).filter(s => {
                            const name = s.name.toLowerCase();
                            const key = roleKey.toLowerCase();
                            if (key === 'lightingsetup') return name.includes('lighting');
                            if (key === 'editor') return name.includes('edit');
                            return name.includes(key);
                        });
                        const maxCount = matchingServices.reduce((sum, s) => sum + (s.qty || 0), 0);
                        const freelancerAssignedCount = enrichedItems.filter(p => p.isAssigned && p.hasStudio !== true).length;
                        const studioAssignedCount = isStudioFulfilled ? Math.max(0, maxCount - freelancerAssignedCount) : 0;
                        const totalAssigned = freelancerAssignedCount + studioAssignedCount;

                        return (
                            <div style={{ marginBottom: "8px", fontSize: "13px", color: "#6b7280" }}>
                                Required: <strong>{maxCount}</strong> | Assigned: <strong>({totalAssigned}/{maxCount})</strong> | Selected: <strong>{visibleSelectedIds.length}</strong> | Studio Assigned: <strong>{studioAssignedCount}</strong> | Freelancers Assigned: <strong>{freelancerAssignedCount}</strong>
                                {isStudioFulfilled && (
                                    <span style={{ color: "#16a34a", marginLeft: "8px", fontWeight: "600" }}>
                                        ✅ Plan fulfilled by Studio
                                    </span>
                                )}
                                {!isStudioFulfilled && visibleSelectedIds.length > maxCount && (
                                    <span style={{ color: "#ef4444", marginLeft: "8px" }}>
                                        (Exceeds plan limit of {maxCount})
                                    </span>
                                )}
                            </div>
                        );
                    })()}
                    </div>

                    {/* Table */}
                    <div
                        className={`hourly-photographer-list${
                            customizeMode || isFlatLayout
                                ? " hourly-photographer-list--full"
                                : ""
                        }`}
                    >
                        {loading ? (
                            <div className="dropdown-status">Loading {title.toLowerCase()}...</div>
                        ) : (
                            <PhotographerTable
                                photographers={enrichedItems}
                                page={page}
                                totalPages={totalPages}
                                LIMIT={LIMIT}
                                isLock={effectiveIsLock}
                                selectAllChecked={selectAllChecked}
                                selectedIds={selectedIds}
                                assignedIds={customizeMode ? enrichedItems.filter(p => p.isAssigned).map(p => cleanId(p._id)) : assignedIdsForRole}
                                otherSelectedIds={otherSelectedIds}
                                onSelectAll={(e) => {
                                    const excluded = [...otherPackagesAssignedIds, ...otherSelectedIds, ...assignedIdsForRole];
                                    
                                    handleSelectAll(e, Infinity, excluded);
                                }}
                                onToggle={handleToggle}
                                onPageChange={setPage}
                                baseAmount={baseAmount}
                                maxCount={
                                    customizeMode
                                        ? 1
                                        : (() => {
                                            const currentPkg = hourlyPackages[selectedPackageIndex];
                                            const matchingServices = (currentPkg?.services || []).filter(s => {
                                                const name = s.name.toLowerCase();
                                                const key = roleKey.toLowerCase();
                                                if (key === 'lightingsetup') return name.includes('lighting');
                                                if (key === 'editor') return name.includes('edit');
                                                return name.includes(key);
                                            });
                                            return matchingServices.reduce((sum, s) => sum + (s.qty || 0), 0);
                                        })()
                                }
                                photographerOverrides={photographerOverrides}
                                setPhotographerOverrides={setPhotographerOverrides}
                            />
                        )}
                    </div>

                    {/* ── Accordion Actions (hidden in customize float — footer handles submit) ── */}
                    {!customizeMode && (() => {
                        const currentPkg = hourlyPackages[selectedPackageIndex];
                        const matchingServices = (currentPkg?.services || []).filter(s => {
                            const name = s.name.toLowerCase();
                            const key = roleKey.toLowerCase();
                            if (key === 'lightingsetup') return name.includes('lighting');
                            if (key === 'editor') return name.includes('edit');
                            return name.includes(key);
                        });
                        const maxCount = customizeMode
                            ? Number.MAX_SAFE_INTEGER
                            : matchingServices.reduce((sum, s) => sum + (s.qty || 0), 0);
                        
                        const hasSelection = selectedIds.length > 0;
                        const isOverQuota = !customizeMode && selectedIds.length > maxCount;

                        return (
                            <div className="accordion-actions" style={{ 
                                display: "flex", 
                                justifyContent: "flex-end",
                                gap: "12px", 
                                marginTop: "24px",
                                paddingTop: "20px",
                                borderTop: "1px solid #f3f4f6" 
                            }}>
                                <button
                                    className="btn-request"
                                    disabled={effectiveIsLock || (!selectAllChecked && !hasSelection)}
                                    onClick={() => handleAction('request')}
                                    style={{
                                        width: "180px",
                                        padding: "10px 16px",
                                        borderRadius: "8px",
                                        border: "none",
                                        backgroundColor: (selectAllChecked || hasSelection) ? "#4a3326" : "#f3f4f6",
                                        color: (selectAllChecked || hasSelection) ? "white" : "#9ca3af",
                                        fontWeight: "700",
                                        fontSize: "12px",
                                        letterSpacing: "0.5px",
                                        textTransform: "uppercase",
                                        cursor: (selectAllChecked || hasSelection) ? "pointer" : "not-allowed",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        gap: "8px",
                                        boxShadow: (selectAllChecked || hasSelection) ? "0 4px 6px -1px rgba(10, 88, 202, 0.2)" : "none",
                                        transition: "all 0.3s ease",
                                        transform: (selectAllChecked || hasSelection) ? "translateY(0)" : "none",
                                    }}
                                    onMouseOver={(e) => {
                                        if (selectAllChecked || hasSelection) {
                                            e.currentTarget.style.backgroundColor = "#4a3326";
                                            e.currentTarget.style.transform = "translateY(-2px)";
                                            e.currentTarget.style.boxShadow = "0 6px 12px -2px rgba(10, 88, 202, 0.3)";
                                        }
                                    }}
                                    onMouseOut={(e) => {
                                        if (selectAllChecked || hasSelection) {
                                            e.currentTarget.style.backgroundColor = "#4a3326";
                                            e.currentTarget.style.transform = "translateY(0)";
                                            e.currentTarget.style.boxShadow = "0 4px 6px -1px rgba(10, 88, 202, 0.2)";
                                        }
                                    }}
                                >
                                     Send Request
                                </button>

                                {isOverQuota && (
                                    <span style={{ alignSelf: "center", fontSize: "12px", color: "#b45309" }}>
                                        Selected exceeds required count.
                                    </span>
                                )}
                            </div>
                        );
                    })()}
                </div>
            )}
        </div>
    );
};

export default RoleAccordion;